import { CommonModule } from '@angular/common';
import { Component, NgZone, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ReportsApiService } from '../../services/reports-api.service';
import { ProductsApiService } from '../../services/products-api.service';
import { UsersApiService } from '../../services/users-api.service';
import { CamelToTitlePipe } from '../../shared/camel-to-title.pipe';
import { reportApiError } from '../../shared/api-result.helper';
import { BASE_VIO_TABLE_V2_CONFIG } from '../../shared/vio-table-v2.config';

interface ReportEntry {
  _id: string;
  productName: string;
  stepName: string;
  operatorName: string;
  mode: string;
  count?: number;
  startTime?: string;
  endTime?: string;
  durationSecs?: number;
  createdAt: string;
}

// The raw report entries have machine-oriented field names (createdAt, durationSecs, …) — this is
// what actually renders in the table, so column headers/values read naturally instead of showing
// ISO timestamps and raw seconds.
interface ReportTableRow {
  Time: string;
  Product: string;
  Step: string;
  Operator: string;
  Mode: string;
  Duration: string;
}

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Module],
  templateUrl: './reports.component.html',
  styleUrl: './reports.component.scss',
})
export class ReportsComponent implements OnInit {
  entries: ReportEntry[] = [];
  tableData: ReportTableRow[] = [];
  products: { _id: string; name: string }[] = [];
  operators: { _id: string; name: string }[] = [];
  loading = false;

  filters = {
    date: new Date().toISOString().slice(0, 10),
    productId: '',
    operatorId: '',
  };

  tableConfig: VT2_TABLE_CONFIG = {
    ...BASE_VIO_TABLE_V2_CONFIG,
    tableTitle: 'Production Log',
    excelFileName: 'production-report',
  };

  private camelToTitle = new CamelToTitlePipe();
  private toastr = inject(ToastrService);

  constructor(
    private reportsApi: ReportsApiService,
    private productsApi: ProductsApiService,
    private usersApi: UsersApiService,
    private ngZone: NgZone,
  ) {}

  async ngOnInit() {
    const [productsRes, usersRes] = await Promise.all([this.productsApi.list(), this.usersApi.list()]);
    // Same zone-continuation issue documented across this app's other load() methods.
    this.ngZone.run(() => {
      if (productsRes.errorCode === VW_ERROR_CODES.SUCCESS) this.products = productsRes.Data;
      else reportApiError(this.toastr, productsRes);
      if (usersRes.errorCode === VW_ERROR_CODES.SUCCESS) this.operators = usersRes.Data.filter((u: any) => u.role === 'operator');
      else reportApiError(this.toastr, usersRes);
    });
    await this.runReport();
  }

  async runReport() {
    this.loading = true;
    const res = await this.reportsApi.getReport({
      date: this.filters.date || undefined,
      productId: this.filters.productId || undefined,
      operatorId: this.filters.operatorId || undefined,
    });
    this.ngZone.run(() => {
      if (res.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.entries = res.Data;
        this.tableData = this.entries.map((entry) => ({
          Time: this.formatTime(entry.createdAt),
          Product: entry.productName,
          Step: entry.stepName,
          Operator: entry.operatorName,
          Mode: this.camelToTitle.transform(entry.mode),
          Duration: this.formatDuration(entry.durationSecs),
        }));
      } else {
        reportApiError(this.toastr, res);
      }
      this.loading = false;
    });
  }

  formatDuration(secs?: number): string {
    if (!secs) return '';
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  }

  formatTime(iso?: string): string {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}
